// Contractor wizard step 1 — find them on the vetting register. RON-5466 Phase 4.
//
// This is the structural difference between inviting a visitor and inviting a contractor. A visitor
// is ad-hoc: you type their name and email and that IS the record. A contractor is someone the
// company has already vetted, so the host does not type their details at all — they look them up,
// and the register decides whether the invite may proceed.
//
// Four outcomes, and only one of them continues straight to the visit details:
//
//   cleared      proceed
//   provisional  proceed, but the visit is flagged so reception does not admit them yet
//   blocked      stop — with the reason, because the host needs to know who to talk to
//   not found    offer to request vetting, which is a different flow and NOT a booking

import { useTranslation } from "react-i18next";

import VisitorFormRow, { fieldClassName } from "./VisitorFormRow.jsx";
import ContractorVettingCard from "./ContractorVettingCard.jsx";
import { isContractorProvisional } from "../visitorConstants.js";

const ContractorLookupStep = ({
  email,
  onEmailChange,
  onLookup,
  isSearching,
  hasSearched,
  contractor,
  error,
  todayISO,
  onRequestVetting,
}) => {
  const { t } = useTranslation("common");

  const notFound = hasSearched && !contractor && !error;
  // Any of the three in-flight states (started / waiting / review) reads as provisional here.
  const provisional = isContractorProvisional(contractor);

  return (
    <>
      <div className="visitor-card">
        <p className="visitor-card__title">{t("react_visitors_contractor_find")}</p>
        <p className="visitor-card__hint">{t("react_visitors_contractor_find_hint")}</p>

        <VisitorFormRow
          id="contractor-email"
          label={`${t("email_address")}*`}
          error={error ? t(error) : null}
        >
          <div className="visitor-lookup">
            <input
              id="contractor-email"
              type="email"
              placeholder={`${t("email_address")}*`}
              className={fieldClassName(email, error)}
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
              // Enter is how anyone actually submits a single-field search.
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  onLookup();
                }
              }}
            />
            <button
              type="button"
              className="visitor-btn visitor-btn--secondary"
              onClick={onLookup}
              disabled={isSearching || !email.trim()}
            >
              {t(isSearching ? "react_loading" : "search")}
            </button>
          </div>
        </VisitorFormRow>
      </div>

      {contractor && (
        <>
          <div className="visitor-card">
            <p className="visitor-card__title">
              {t("react_visitors_contractor_found")}
            </p>
            <div className="visitor-detail-row">
              <span className="visitor-detail-row__label">{t("name")}</span>
              <span className="visitor-detail-row__value">{contractor.name}</span>
            </div>
            <div className="visitor-detail-row">
              <span className="visitor-detail-row__label">{t("company")}</span>
              <span className="visitor-detail-row__value">{contractor.company}</span>
            </div>
            {contractor.trade && (
              <div className="visitor-detail-row">
                <span className="visitor-detail-row__label">
                  {t("react_visitors_field_trade")}
                </span>
                <span className="visitor-detail-row__value">{contractor.trade}</span>
              </div>
            )}
          </div>

          <ContractorVettingCard contractor={contractor} todayISO={todayISO} />

          {/* No separate card for the blocked case: the vetting card above already shows the
              Blocked badge and the reason, and Next is disabled. Provisional DOES get one, because
              the badge states the status while this states the consequence — bookable, but they
              will not be admitted. */}
          {provisional && (
            <div className="visitor-card visitor-card--warning">
              <p className="visitor-vetting__reason">
                {t("react_visitors_vetting_provisional_message")}
              </p>
            </div>
          )}
        </>
      )}

      {/* Not being on the register is the normal case for someone new, so it reads as a next step
          rather than a failure. */}
      {notFound && (
        <div className="visitor-card">
          <p className="visitor-card__title">
            {t("react_visitors_contractor_not_found")}
          </p>
          <p className="visitor-card__hint">
            {t("react_visitors_contractor_not_found_hint")}
          </p>
          <button
            type="button"
            className="visitor-btn visitor-btn--secondary"
            onClick={onRequestVetting}
          >
            {t("react_visitors_request_vetting")}
          </button>
        </div>
      )}
    </>
  );
};

export default ContractorLookupStep;
